/**
 * Shared Zod schemas + TS types. Kept in sync with apps/api/src/shared/types.ts.
 * (Manual copy — npm workspaces don't survive Windows symlink restrictions in
 * many setups, so we duplicate instead of sharing a workspace package.)
 */
import { z } from 'zod';

// --- auth ---
export const UserRole = z.enum(['admin', 'cobrador', 'cliente']);
export type UserRole = z.infer<typeof UserRole>;

export const UserStatus = z.enum(['active', 'disabled']);
export type UserStatus = z.infer<typeof UserStatus>;

export const LoginInput = z.object({
  email: z.string().email().max(255),
  password: z.string().min(8).max(128),
});
export type LoginInput = z.infer<typeof LoginInput>;

export const SessionUser = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  role: UserRole,
});
export type SessionUser = z.infer<typeof SessionUser>;

export const LoginResponse = z.object({
  user: SessionUser,
});
export type LoginResponse = z.infer<typeof LoginResponse>;

// --- snapshot ---
export const ClientStatus = z.enum(['al_dia', 'en_gracia', 'caducado']);
export type ClientStatus = z.infer<typeof ClientStatus>;

export const DeudaItem = z.object({
  deuda_id: z.number().int(),
  mes_id: z.number().int(),
  year: z.number().int(),
  fecha: z.string(),
  cantidad: z.number(),
  pagado: z.boolean().or(z.number()),
});
export type DeudaItem = z.infer<typeof DeudaItem>;

export const SnapshotSummary = z.object({
  total_adeudado: z.number(),
  dias_vencidos: z.number().int(),
  dias_gracia_restantes: z.number().int(),
  estado: ClientStatus,
});
export type SnapshotSummary = z.infer<typeof SnapshotSummary>;

export const StatusCounts = z.object({
  active: z.number().int().default(0),
  blocked: z.number().int().default(0),
  other: z.number().int().default(0),
});
export type StatusCounts = z.infer<typeof StatusCounts>;

export const ClientSnapshot = z.object({
  db_name: z.string(),
  year: z.number().int(),
  system_title: z.string(),
  alargar_dias: z.number().int(),
  teacher_alert_enabled: z.boolean().or(z.number()),
  admin_status: StatusCounts,
  teacher_status: StatusCounts,
  deudas: z.array(DeudaItem),
  summary: SnapshotSummary,
});
export type ClientSnapshot = z.infer<typeof ClientSnapshot>;

// --- mutation inputs ---
export const PagarInput = z.object({
  mes_id: z.number().int().positive(),
});
export type PagarInput = z.infer<typeof PagarInput>;

export const RevertirInput = z.object({
  mes_id: z.number().int().positive(),
  cantidad: z.number().positive().optional(),
});
export type RevertirInput = z.infer<typeof RevertirInput>;

export const DiasInput = z.object({
  dias: z.number().int().min(0).max(365),
});
export type DiasInput = z.infer<typeof DiasInput>;

export const AddDeudaInput = z.object({
  fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'fecha must be YYYY-MM-DD'),
  cantidad: z.number().positive(),
});
export type AddDeudaInput = z.infer<typeof AddDeudaInput>;

// --- cache rows (api → web) ---
export const ClientCacheRow = z.object({
  id: z.string().uuid(),
  server_id: z.string().uuid(),
  server_name: z.string(),
  db_name: z.string(),
  year: z.number().int(),
  dominio: z.string(),
  system_title: z.string(),
  total_adeudado: z.number(),
  dias_vencidos: z.number().int(),
  dias_gracia_restantes: z.number().int(),
  estado: ClientStatus,
  last_sync_at: z.string().nullable(),
});
export type ClientCacheRow = z.infer<typeof ClientCacheRow>;
